/**
 * AnalisisRiesgos - Visualización del análisis de riesgos de una evaluación
 * Features:
 * - Indicador de nivel de riesgo
 * - Gráfica de cumplimiento por sección
 * - Lista de ítems críticos
 * - Recomendaciones
 * - Opción de descarga (futuro)
 */
import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  FileText,
  Download,
} from 'lucide-react';
import { chequeoAPI } from '../../api';
import { Button, Loading, Alert } from '../common';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const AnalisisRiesgos = ({ evaluacionId }) => {
  const [analisis, setAnalisis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalisis();
  }, [evaluacionId]);

  const loadAnalisis = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await chequeoAPI.getAnalisis(evaluacionId);
      setAnalisis(response.data);
    } catch (err) {
      console.error('Error al cargar análisis:', err);
      setError(err.message || 'Error al cargar análisis');
    } finally {
      setLoading(false);
    }
  };

  const getNivelRiesgoInfo = (nivel) => {
    const niveles = {
      Alto: {
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-500',
        icon: '🔴',
        descripcion: 'Se requiere acción inmediata',
      },
      Medio: {
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-500',
        icon: '🟡',
        descripcion: 'Se recomienda atención prioritaria',
      },
      Bajo: {
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-500',
        icon: '🟢',
        descripcion: 'Nivel de riesgo aceptable',
      },
    };

    return niveles[nivel] || niveles.Medio;
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Analizando evaluación..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        title="Error"
        message={error}
      />
    );
  }

  if (!analisis) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No se pudo cargar el análisis</p>
      </div>
    );
  }

  const riesgoInfo = getNivelRiesgoInfo(analisis.nivel_riesgo);
  const porcentajeCumplimiento = Math.round(
    (analisis.respuestas_si / analisis.total_items) * 100
  );

  // Preparar datos para la gráfica
  const dataPorSeccion = analisis.analisis_por_seccion || [];
  const chartData = dataPorSeccion.map((s) => ({
    name: `Sección ${s.seccion}`,
    cumplimiento: Math.round((s.respuestas_si / s.total_items) * 100),
    items_criticos: s.items_criticos || 0,
  }));

  // Colores para las barras según nivel
  const getBarColor = (value) => {
    if (value >= 80) return '#10b981'; // green-500
    if (value >= 50) return '#f59e0b'; // yellow-500
    return '#ef4444'; // red-500
  };

  return (
    <div className="space-y-6">
      {/* Indicador principal de riesgo */}
      <div
        className={`${riesgoInfo.bgColor} ${riesgoInfo.borderColor} border-l-4 p-6 rounded-r-lg`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{riesgoInfo.icon}</span>
            <div>
              <h3 className={`text-2xl font-bold ${riesgoInfo.textColor}`}>
                RIESGO {analisis.nivel_riesgo.toUpperCase()}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {riesgoInfo.descripcion}
              </p>
            </div>
          </div>
        </div>

        {/* Métricas principales */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">
                Cumplimiento
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {porcentajeCumplimiento}%
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {analisis.respuestas_si} de {analisis.total_items} ítems
            </p>
          </div>

          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">
                Respuestas Sí
              </span>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {analisis.respuestas_si}
            </p>
          </div>

          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-gray-600">
                Respuestas No
              </span>
            </div>
            <p className="text-3xl font-bold text-red-600">
              {analisis.respuestas_no}
            </p>
          </div>
        </div>
      </div>

      {/* Gráfica de cumplimiento por sección */}
      {chartData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Cumplimiento por Sección
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Cumplimiento (%)', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="cumplimiento" name="Cumplimiento (%)">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.cumplimiento)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Ítems críticos */}
      {analisis.items_criticos && analisis.items_criticos.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h4 className="text-lg font-semibold text-gray-900">
              Ítems Críticos ({analisis.items_criticos.length})
            </h4>
          </div>
          <div className="space-y-3">
            {analisis.items_criticos.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg"
              >
                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{item.descripcion}</p>
                  {item.riesgo_dolor && (
                    <p className="text-sm text-gray-600 mt-1">{item.riesgo_dolor}</p>
                  )}
                  {item.comentario && (
                    <p className="text-sm text-gray-500 mt-1 italic">
                      Comentario: {item.comentario}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recomendaciones */}
      {analisis.recomendaciones && analisis.recomendaciones.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-blue-600" />
            <h4 className="text-lg font-semibold text-blue-900">
              Recomendaciones
            </h4>
          </div>
          <ul className="space-y-2">
            {analisis.recomendaciones.map((rec, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">{index + 1}.</span>
                <span className="text-gray-700">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Conclusión */}
      {analisis.conclusion && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Conclusión
          </h4>
          <p className="text-gray-700">{analisis.conclusion}</p>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button
          variant="outline"
          icon={Download}
          onClick={() => {
            // TODO: Implementar descarga de PDF
            alert('Función de descarga en desarrollo');
          }}
        >
          Descargar PDF
        </Button>
        <Button
          variant="primary"
          onClick={() => window.print()}
        >
          Imprimir
        </Button>
      </div>
    </div>
  );
};

export default AnalisisRiesgos;
